import { useRef, useState } from "react";
import { FileUp, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { nome: "", cognome: "", data_nascita: "", codice_fiscale: "", tribunale: "", numero_rg: "", data_convalida: "" };
const FIELDS = [
  ["nome", "Nome inquilino *", "text", true], ["cognome", "Cognome *", "text", true],
  ["data_nascita", "Data di nascita *", "date", true], ["codice_fiscale", "Codice fiscale", "text", false],
  ["tribunale", "Tribunale di competenza *", "text", true], ["numero_rg", "Numero R.G. *", "text", true],
  ["data_convalida", "Data convalida *", "date", true],
];

export const ReportForm = ({ onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf")) return toast.error("Carica un file PDF");
    if (f.size > 10 * 1024 * 1024) return toast.error("Il PDF non può superare i 10 MB");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Il PDF del provvedimento è obbligatorio");
    if (!accepted) return toast.error("Devi accettare la dichiarazione di veridicità");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("dichiarazione", "true");
    fd.append("pdf", file);
    setLoading(true);
    try {
      await api.post("/reports", fd);
      toast.success("Segnalazione inviata: sarà visibile dopo la verifica del provvedimento");
      setForm(EMPTY); setFile(null); setAccepted(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="report-form" className="surface grid gap-4 p-6 sm:grid-cols-2 sm:p-8">
      {FIELDS.map(([k, label, type, req]) => (
        <div key={k} className={k === "tribunale" ? "sm:col-span-2" : ""}>
          <label className="field-label">{label}</label>
          <input data-testid={`report-input-${k}`} type={type} required={req} className={`field-input ${k === "codice_fiscale" ? "uppercase" : ""}`}
            placeholder={k === "tribunale" ? "es. Tribunale di Milano" : k === "numero_rg" ? "es. 4821/2025" : ""}
            value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
        </div>
      ))}
      <div className="sm:col-span-2">
        <label className="field-label">Provvedimento di convalida (PDF) *</label>
        {file ? (
          <div data-testid="report-file-preview" className="flex items-center gap-3 rounded-xl border border-[#2563eb] bg-blue-50 p-4">
            <FileText className="h-8 w-8 text-[#1e3a8a]" />
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-[#0f172a]">{file.name}</p><p className="text-xs text-slate-500">{(file.size / 1024).toFixed(0)} KB</p></div>
            <button type="button" data-testid="button-remove-pdf" onClick={() => setFile(null)} className="text-slate-500 hover:text-[#dc2626]"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <button type="button" data-testid="button-upload-eviction-pdf" onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
            className={`flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed p-8 text-sm ${drag ? "border-[#2563eb] bg-blue-50" : "border-slate-300 hover:border-[#2563eb]"}`}
            style={{ transition: "border-color 160ms, background-color 160ms" }}>
            <FileUp className="h-7 w-7 text-[#1e3a8a]" />
            <span className="font-semibold text-[#0f172a]">Trascina qui il PDF o clicca per caricarlo</span>
            <span className="text-xs text-slate-500">Solo PDF del provvedimento convalidato dal giudice · max 10 MB</span>
          </button>
        )}
        <input ref={inputRef} data-testid="input-eviction-pdf" type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
      </div>
      <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-600 sm:col-span-2">
        <Checkbox data-testid="report-checkbox-dichiarazione" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" />
        Dichiaro sotto la mia responsabilità la veridicità dell'atto allegato e l'osservanza del Regolamento UE 2016/679 (GDPR).
      </label>
      <button data-testid="report-button-submit" type="submit" disabled={loading} className="btn-accent sm:col-span-2">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}Invia segnalazione
      </button>
    </form>
  );
};
